import {
  IconButton,
  Modal,
  SingleSelectDropdown,
  Text,
  type DropdownItem,
} from "@carbon6/c6-frontend-design";
import { useState, type ReactNode } from "react";
import type { WorkspaceDrift } from "@/api";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import styles from "./RemapConfirmModal.module.css";

type RemapConfirmModalProps = {
  // 소유자를 이전할 드리프트 행(표시명·현 소유자 표기용).
  drift: WorkspaceDrift;
  memberOptions: readonly DropdownItem[];
  // 이전 진행 중 여부. true 면 버튼을 잠그고 백드롭/Esc 도 잠근다.
  busy: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onConfirm: (memberUuid: string) => void;
};

// 소유자 이전 확인. 사용자가 새 소유자를 고르고 즉시 전체 반영 경고를 확인하면 remap 을 보낸다.
// 스냅샷도 함께 갱신된다(소유자 이전 remap 의 계약). 제출 상태는 부모(패널)가 전달한다.
export function RemapConfirmModal({
  drift,
  memberOptions,
  busy,
  errorMessage,
  onClose,
  onConfirm,
}: RemapConfirmModalProps): ReactNode {
  const [memberUuid, setMemberUuid] = useState<string | null>(null);
  const actionDisabled = busy || memberUuid === null;

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      aria-labelledby="remap-confirm-modal-title"
    >
      <HatModalHeader
        title="Workspace 소유자를 이전합니다."
        titleId="remap-confirm-modal-title"
        close={{ disabled: busy }}
      />
      <div className={styles.body}>
        <Text variant={hatcheryTextModes["body-normal"]}>
          {`"${drift.display_name}"의 소유자를 ${drift.owner.display_name} 님에게서 이전합니다.`}
        </Text>
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.default}
          label="새 소유자"
          value={memberUuid}
          options={[...memberOptions]}
          onValueChange={setMemberUuid}
          placeholder="멤버를 선택해 주세요."
          disabled={busy}
        />
        <Text variant={hatcheryTextModes["body-normal"]} className={styles.warning}>
          소유자를 이전하면 이 Workspace의 과거·미래 Session과 Evaluation·Policy 가시성이 저장 즉시
          새 소유자에게 반영됩니다.
        </Text>
        {errorMessage ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.error} role="alert">
            {errorMessage}
          </Text>
        ) : null}
        <div className={styles.actions}>
          <IconButton
            text="취소"
            type="button"
            variant="surface"
            disabled={busy}
            onClick={onClose}
          />
          <IconButton
            text={busy ? "이전 중…" : "소유자 이전"}
            type="button"
            variant="solid"
            tone="danger"
            disabled={actionDisabled}
            onClick={() => {
              if (memberUuid !== null) {
                onConfirm(memberUuid);
              }
            }}
          />
        </div>
      </div>
    </Modal>
  );
}
